import { permissionMessages } from 'src/features/auth/utils';

export type RegisterResponse = {
    id: string;
    email: string;
    name: string;
};

export type LoginResponse = {
    access: string;
    refresh: string;
    user: User;
};

export type TokenResponse = {
    access: string;
};

export type TokenErrorResponse = {
    detail: string;
    code: string;
};

export type Token = {
    token_type: 'access' | 'refresh';
    exp: number;
    iat: number;
    jti: string;
    sub: string;
    id: string;
};

export interface User {
    email: string;
    first_name: string;
    last_name: string;
    name: string;
    id: string;
    lang: string;
    is_superuser: boolean;
}

enum PERMISSION {
    ACCESS_ENTITIES = 'access_entities',
    MANAGE_ENTITIES = 'manage_entities',
    MANAGE_SERVICE_PROVIDERS = 'manage_service_providers',
    ACCESS_SITES = 'access_sites',
    MANAGE_SITES = 'manage_sites',
    MANAGE_ZONES = 'manage_zones',
    MANAGE_GATEWAYS = 'manage_gateways',
    MANAGE_SPACES = 'manage_spaces',
    MANAGE_ROLES = 'manage_roles',
    ACCESS_ROLES = 'access_roles',
    ACCESS_USERS = 'access_users',
    MANAGE_USERS = 'manage_users',
    MANAGE_SYSTEM_SETTINGS = 'manage_system_settings',
    MANAGE_SYSTEM_COMPONENTS = 'manage_system_components',
    ACCESS_MEMBERSHIPS = 'access_memberships',
    MANAGE_MEMBERSHIPS = 'manage_memberships',
    ACCESS_MEMBERSHIP_TYPES = 'access_membership_types',
    MANAGE_MEMBERSHIP_TYPES = 'manage_membership_types',
    ACCESS_GATEWAY_PASSES = 'access_gateway_passes',
    MANAGE_ACCESS_KEYS = 'manage_access_keys',
    ACCESS_ACTIVE_STAYS = 'access_active_stays',
    MANAGE_ACTIVE_STAYS = 'manage_active_stays',
    ACCESS_PAST_STAYS = 'access_past_stays',
    ACCESS_VISITORS = 'access_visitors',
    ACCESS_PRICING_PLANS = 'access_pricing_plans',
    MANAGE_PRICING_PLANS = 'manage_pricing_plans',
    ACCESS_ACCESS_KEYS = 'access_access_keys',
    ACCESS_PAY_STATIONS = 'access_pay_stations',
    MANAGE_PAY_STATIONS = 'manage_pay_stations',
    MANAGE_VALIDATIONS = 'manage_validations',
    ALLOW_VALIDATIONS = 'allow_validation',
    ACCESS_PAYMENTS = 'access_payments',
    ACCESS_PAY_STATION_OPERATIONS = 'access_pay_station_operations',
    ACCESS_PAY_STATION_EVENTS = 'access_pay_station_events',
    ACCESS_SERVICE_PROVIDERS = 'access_service_providers',
    ACCESS_ZONES = 'access_zones',
    ACCESS_GATEWAYS = 'access_gateways',
    ACCESS_SPACES = 'access_spaces',
    ACCESS_TROUBLESHOOTING = 'access_troubleshooting',
    ACCESS_PRICING_PLAN_TICK_RATES = 'access_pricing_plan_tick_rates',
    MANAGE_PRICING_PLAN_TICK_RATES = 'manage_pricing_plan_tick_rates',
    ACCESS_PRICING_PLAN_TIERS = 'access_pricing_plan_tiers',
    MANAGE_PRICING_PLAN_TIERS = 'manage_pricing_plan_tiers',
    ACCESS_PRICING_PLAN_CAPS = 'access_pricing_plan_caps',
    MANAGE_PRICING_PLAN_CAPS = 'manage_pricing_plan_caps',
    ACCESS_PRICING_PLAN_PERIOD_ADJUSTMENTS = 'access_pricing_plan_period_adjustments',
    MANAGE_PRICING_PLAN_PERIOD_ADJUSTMENTS = 'manage_pricing_plan_period_adjustments',
    ACCESS_GATEWAYS_MONITORING = 'access_gateways_monitoring',
    ACCESS_GATEWAYS_MONITORING_ACTIONS = 'access_gateways_monitoring_actions',
    ACCESS_PAY_STATIONS_MONITORING = 'access_pay_stations_monitoring',
    ACCESS_DEVICES_MONITORING = 'access_devices_monitoring',
    ACCESS_COMPONENTS_HEALTH = 'access_components_health',
    ACCESS_PAYMENT_STATISTICS = 'access_payment_statistics',
    ACCESS_OCCUPANCY_STATISTICS = 'access_occupancy_statistics',
    ACCESS_HOME_STATS = 'access_home_stats',
    ACCESS_EVENT_LOGS = 'access_event_logs',
    ACCESS_VALIDATION_STATISTICS = 'access_validation_statistics',
    MANAGE_API_KEYS = 'manage_api_keys',
    ACCESS_ALLOW_LISTS = 'access_allow_lists',
    MANAGE_ALLOW_LISTS = 'manage_allow_lists'
}

const ENTITY_PERMISSION = [
    {
        value: PERMISSION.ACCESS_ENTITIES,
        label: permissionMessages.accessEntities,
    },
    {
        value: PERMISSION.MANAGE_ENTITIES,
        label: permissionMessages.manageEntities,
    },
];

const SITE_PERMISSION = [
    {
        value: PERMISSION.ACCESS_SERVICE_PROVIDERS,
        label: permissionMessages.accessServiceProviders,
    },
    {
        value: PERMISSION.MANAGE_SERVICE_PROVIDERS,
        label: permissionMessages.manageServiceProviders,
    },
    {
        value: PERMISSION.ACCESS_SITES,
        label: permissionMessages.accessSites,
    },
    {
        value: PERMISSION.MANAGE_SITES,
        label: permissionMessages.manageSites,
    },
    {
        value: PERMISSION.ACCESS_ZONES,
        label: permissionMessages.accessZones,
    },
    {
        value: PERMISSION.MANAGE_ZONES,
        label: permissionMessages.manageZones,
    },
    {
        value: PERMISSION.ACCESS_GATEWAYS,
        label: permissionMessages.accessGateways,
    },
    {
        value: PERMISSION.MANAGE_GATEWAYS,
        label: permissionMessages.manageGateways,
    },
    {
        value: PERMISSION.ACCESS_SPACES,
        label: permissionMessages.accessSpaces,
    },
    {
        value: PERMISSION.MANAGE_SPACES,
        label: permissionMessages.manageSpaces,
    },
];

const USER_PERMISSION = [
    {
        value: PERMISSION.ACCESS_ROLES,
        label: permissionMessages.accessRoles,
    },
    {
        value: PERMISSION.MANAGE_ROLES,
        label: permissionMessages.manageRoles,
    },
    {
        value: PERMISSION.ACCESS_USERS,
        label: permissionMessages.accessUsers,
    },
    {
        value: PERMISSION.MANAGE_USERS,
        label: permissionMessages.manageUsers,
    },
];

const SYSTEM_PERMISSION = [
    {
        value: PERMISSION.MANAGE_SYSTEM_SETTINGS,
        label: permissionMessages.manageSystemSettings,
    },
    {
        value: PERMISSION.MANAGE_SYSTEM_COMPONENTS,
        label: permissionMessages.manageSystemComponents,
    },
    {
        value: PERMISSION.MANAGE_API_KEYS,
        label: permissionMessages.manageApiKeys,
    },
];

const MEMBERSHIP_PERMISSION = [
    {
        value: PERMISSION.ACCESS_MEMBERSHIPS,
        label: permissionMessages.accessMemberships,
    },
    {
        value: PERMISSION.MANAGE_MEMBERSHIPS,
        label: permissionMessages.manageMemberships,
    },
    {
        value: PERMISSION.ACCESS_MEMBERSHIP_TYPES,
        label: permissionMessages.accessMembershipTypes,
    },
    {
        value: PERMISSION.MANAGE_MEMBERSHIP_TYPES,
        label: permissionMessages.manageMembershipTypes,
    },
];

const VISITOR_PERMISSION = [
    {
        value: PERMISSION.ACCESS_ACTIVE_STAYS,
        label: permissionMessages.accessActiveStays,
    },
    {
        value: PERMISSION.MANAGE_ACTIVE_STAYS,
        label: permissionMessages.manageActiveStays,
    },
    {
        value: PERMISSION.ACCESS_PAST_STAYS,
        label: permissionMessages.accessPastStays,
    },
    {
        value: PERMISSION.ACCESS_GATEWAY_PASSES,
        label: permissionMessages.accessGatewayPasses,
    },
    {
        value: PERMISSION.ACCESS_VISITORS,
        label: permissionMessages.accessVisitors,
    },
    {
        value: PERMISSION.ACCESS_EVENT_LOGS,
        label: permissionMessages.accessEventLog,
    },
];

const ACCESS_KEYS_PERMISSION = [
    {
        value: PERMISSION.ACCESS_ACCESS_KEYS,
        label: permissionMessages.accessAccessKeys,
    },
    {
        value: PERMISSION.MANAGE_ACCESS_KEYS,
        label: permissionMessages.manageAccessKeys,
    },
];

const TROUBLESHOOTING_PERMISSION = [
    {
        value: PERMISSION.ACCESS_TROUBLESHOOTING,
        label: permissionMessages.accessTroubleshooting,
    },
];

const ALLOW_LISTS_PERMISSION = [
    {
        value: PERMISSION.ACCESS_ALLOW_LISTS,
        label: permissionMessages.accessAllowLists,
    },
    {
        value: PERMISSION.MANAGE_ALLOW_LISTS,
        label: permissionMessages.manageAllowLists,
    },
];

const VALIDATIONS_PERMISSION = [
    {
        value: PERMISSION.ALLOW_VALIDATIONS,
        label: permissionMessages.allowValidations,
    },
    {
        value: PERMISSION.MANAGE_VALIDATIONS,
        label: permissionMessages.manageValidations,
    },
];

const MONITORING_PERMISSION = [
    {
        value: PERMISSION.ACCESS_GATEWAYS_MONITORING,
        label: permissionMessages.accessGatewaysMonitoring,
    },
    {
        value: PERMISSION.ACCESS_GATEWAYS_MONITORING_ACTIONS,
        label: permissionMessages.accessGatewaysMonitoringActions,
    },
    {
        value: PERMISSION.ACCESS_PAY_STATIONS_MONITORING,
        label: permissionMessages.accessPayStationsMonitoring,
    },
    {
        value: PERMISSION.ACCESS_DEVICES_MONITORING,
        label: permissionMessages.accessDevicesMonitoring,
    },
    {
        value: PERMISSION.ACCESS_COMPONENTS_HEALTH,
        label: permissionMessages.accessComponentsHealth,
    },
];

const HOME_SCREEN_PERMISSION = [
    {
        value: PERMISSION.ACCESS_HOME_STATS,
        label: permissionMessages.accessHomeStats,
    },
];

const STATISTICS_PERMISSION = [
    {
        value: PERMISSION.ACCESS_PAYMENT_STATISTICS,
        label: permissionMessages.accessPaymentStatistics,
    },
    {
        value: PERMISSION.ACCESS_OCCUPANCY_STATISTICS,
        label: permissionMessages.accessOccupancyStatistics,
    },
    {
        value: PERMISSION.ACCESS_VALIDATION_STATISTICS,
        label: permissionMessages.accessValidationStatistics,
    },
];

const PRICING_PLANS_PERMISSION = [
    {
        value: PERMISSION.ACCESS_PRICING_PLANS,
        label: permissionMessages.accessPricingPlans,
    },
    {
        value: PERMISSION.MANAGE_PRICING_PLANS,
        label: permissionMessages.managePricingPlans,
    },
    {
        value: PERMISSION.ACCESS_PRICING_PLAN_TICK_RATES,
        label: permissionMessages.accessPricingPlanTickRate,
    },
    {
        value: PERMISSION.MANAGE_PRICING_PLAN_TICK_RATES,
        label: permissionMessages.managePricingPlanTickRate,
    },
    {
        value: PERMISSION.ACCESS_PRICING_PLAN_TIERS,
        label: permissionMessages.accessPricingPlanTiers,
    },
    {
        value: PERMISSION.MANAGE_PRICING_PLAN_TIERS,
        label: permissionMessages.managePricingPlanTiers,
    },
    {
        value: PERMISSION.ACCESS_PRICING_PLAN_CAPS,
        label: permissionMessages.accessPricingPlanCaps,
    },
    {
        value: PERMISSION.MANAGE_PRICING_PLAN_CAPS,
        label: permissionMessages.managePricingPlanCaps,
    },
    {
        value: PERMISSION.ACCESS_PRICING_PLAN_PERIOD_ADJUSTMENTS,
        label: permissionMessages.accessPricingPlanPeriodAdjustments,
    },
    {
        value: PERMISSION.MANAGE_PRICING_PLAN_PERIOD_ADJUSTMENTS,
        label: permissionMessages.managePricingPlanPeriodAdjustments,
    },
];

const PAYMENTS_PERMISSION = [
    {
        value: PERMISSION.ACCESS_PAYMENTS,
        label: permissionMessages.accessPayments,
    },
    {
        value: PERMISSION.ACCESS_PAY_STATIONS,
        label: permissionMessages.accessPayStations,
    },
    {
        value: PERMISSION.MANAGE_PAY_STATIONS,
        label: permissionMessages.managePayStations,
    },
    {
        value: PERMISSION.ACCESS_PAY_STATION_OPERATIONS,
        label: permissionMessages.accessPayStationOperations,
    },
    {
        value: PERMISSION.ACCESS_PAY_STATION_EVENTS,
        label: permissionMessages.accessPayStationEvents,
    },
];

export {
    PERMISSION,
    ENTITY_PERMISSION,
    SITE_PERMISSION,
    USER_PERMISSION,
    SYSTEM_PERMISSION,
    MEMBERSHIP_PERMISSION,
    VISITOR_PERMISSION,
    PRICING_PLANS_PERMISSION,
    ACCESS_KEYS_PERMISSION,
    TROUBLESHOOTING_PERMISSION,
    ALLOW_LISTS_PERMISSION,
    VALIDATIONS_PERMISSION,
    HOME_SCREEN_PERMISSION,
    MONITORING_PERMISSION,
    STATISTICS_PERMISSION,
    PAYMENTS_PERMISSION,
};
